import { motion } from 'framer-motion';
import { SiteConfig } from '../types/portfolio';
import SectionHeading from './SectionHeading';

type ExperienceTimelineProps = {
  experience: SiteConfig['experience'];
};

function ExperienceTimeline({ experience }: ExperienceTimelineProps) {
  return (
    <section id="experience" className="section-shell">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Experience"
          title="Hands-on delivery across enterprise applications and platform workflows."
          description="A timeline focused on architecture, implementation quality, Agile collaboration, and reliable delivery for production software systems."
        />

        <div className="relative ml-3 border-l border-white/10 pl-8">
          {experience.map((item, index) => (
            <motion.article
              key={`${item.company}-${item.duration}`}
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="glass-panel relative mb-8 rounded-[2rem] p-8 last:mb-0"
            >
              <span className="absolute -left-[2.45rem] top-10 h-4 w-4 rounded-full border-4 border-slate-950 bg-sky-400" />
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.3em] text-sky-200">
                    {item.company}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold text-white">{item.role}</h3>
                </div>
                <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200">
                  {item.duration}
                </span>
              </div>

              <ul className="mt-6 space-y-4 text-sm leading-7 text-slate-300 sm:text-base">
                {item.responsibilities.map((responsibility) => (
                  <li key={responsibility} className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-teal-300" />
                    <span>{responsibility}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-wrap gap-3">
                {item.stack.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-full border border-white/10 bg-slate-900/80 px-3 py-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-200"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ExperienceTimeline;
